import React, { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { FormattedMessage } from "react-intl";
import type {
    InfiniteData,
    QueryObserverResult,
    RefetchOptions,
    RefetchQueryFilters,
} from "react-query";
import { useMutation, useQueryClient } from "react-query";

import { addTrainee, addTraineeBulk } from "@/api/trainee";
import { useModal } from "@/context/modal/modal";

import { AddUsersEmailErrorAPIResponse } from "@/pages/users/components/options-bar/options-bar.types";

import GenericError from "@/components/ui/generic-error";
import Loader from "@/components/ui/loader";

import {
    ButtonsWrapper,
    Description,
    ModalContentWrapperDiv,
    StyledConfirmButton,
    StyledInput,
    Title,
} from "../../modal-contents.styles";

import AddUserNotSuccessfulModalContent from "../add-user-not-successful/add-user-not-successful-modal-content";
import ConfirmationModalContent from "../confirmation/confirmation-modal-content";

export interface AddUserErrorAPIResponse {
    propertyName: string;
    errorMessage: string;
    attemptedValue: string;
    errorCode: string;
}

export interface NotCreatedUsersResponse {
    emailAddress: string;
    errorMessage: string;
    errorCode: string;
}
export interface AddMultipleUsersSuccessfulAPIResponse {
    createdUsers: string[];
    notCreatedUsers: NotCreatedUsersResponse[];
}

interface AddUserModalContentProps {
    title: string;
    description: string;
    refetchUsersData?: <TPageData>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
    ) => Promise<QueryObserverResult<InfiniteData<any>, unknown>>;
}

const AddUserModalContent = ({
    title,
    description,
    refetchUsersData,
}: AddUserModalContentProps) => {
    const { openModal, setMinHeight, setWidth } = useModal();

    const queryClient = useQueryClient();

    const [inputValue, setInputValue] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    //this useEffect is for the input field to focus when the modal is open
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const mutationForSingleUser = useMutation(addTrainee, {
        onSuccess: () => {
            queryClient.invalidateQueries(["getUsers"]); // Invalidates cached data associated with the "getUsers" query key

            openModal({
                content: (
                    <ConfirmationModalContent
                        title="users:users-table:modal-contents:add-user:confirmation-modal:title"
                        description="users:users-table:modal-contents:add-user:confirmation-modal:description"
                        isAddUserConfirmationModal
                    />
                ),
            });

            refetchUsersData && refetchUsersData();
        },
        onError: (error: any) => {
            const internalServerError = error.response === undefined;

            const existingEmailError =
                error.response.status === 400 &&
                error.response.data[0].errorCode ===
                    AddUsersEmailErrorAPIResponse.EmailAlreadyExists;

            const invalidEmailError =
                error.response.status === 400 &&
                error.response.data[0].errorCode === AddUsersEmailErrorAPIResponse.InvalidEmail;

            if (internalServerError) {
                openModal({ content: <GenericError /> });
            }

            if (existingEmailError) {
                openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:repeated-email"
                            addSingleUserErrorAPIResponse={error.response.data}
                        />
                    ),
                });
            } else if (invalidEmailError) {
                openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:invalid-email"
                        />
                    ),
                });
            } else {
                openModal({ content: <GenericError /> });
            }
        },
    });

    const mutationForMultipleUsers = useMutation(addTraineeBulk, {
        onSuccess: (response: AddMultipleUsersSuccessfulAPIResponse) => {
            queryClient.invalidateQueries(["getUsers"]); // Invalidates cached data associated with the "getUsers" query key

            const successInCreatingAllUsers =
                response.createdUsers.length > 0 && response.notCreatedUsers.length === 0;
            const onlySomeUsersAdded =
                response.createdUsers.length > 0 && response.notCreatedUsers.length > 0;
            const noUsersAdded = response.createdUsers.length === 0;

            if (noUsersAdded) {
                setWidth("540px");
                setMinHeight("352px");
                //if no users are added, there's no need to updateUsersList

                return openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:repeated-email"
                            addMultipleUsersSuccessfulAPIResponse={response}
                        />
                    ),
                });
            }

            if (successInCreatingAllUsers) {
                openModal({
                    content: (
                        <ConfirmationModalContent
                            title="users:users-table:modal-contents:add-user:confirmation-modal:title"
                            description="users:users-table:modal-contents:add-user:confirmation-modal:description"
                            isAddUserConfirmationModal
                        />
                    ),
                });

                refetchUsersData && refetchUsersData();
            }

            if (onlySomeUsersAdded) {
                setWidth("540px");
                setMinHeight("352px");

                openModal({
                    content: (
                        <ConfirmationModalContent
                            title="users:users-table:modal-contents:add-user:confirmation-modal:not-all-users-added:title"
                            description="users:users-table:modal-contents:add-user:confirmation-modal:description"
                            isAddUserConfirmationModal
                            addMultipleUsersSuccessfulAPIResponse={response}
                        />
                    ),
                });

                refetchUsersData && refetchUsersData();
            }
        },
        onError: (error: any) => {
            const internalServerError = error.response === undefined;

            const invalidEmailError =
                error.response.status === 400 &&
                error.response.data[0].errorCode === AddUsersEmailErrorAPIResponse.InvalidEmail;

            const maximumSizeReachedError =
                error.response.status === 400 &&
                error.response.data[0].errorCode === AddUsersEmailErrorAPIResponse.MaxiSizeReached;

            const duplicateEmailsError =
                error.response.status === 400 &&
                error.response.data[0].errorCode === AddUsersEmailErrorAPIResponse.DuplicateEmails;

            if (internalServerError) {
                openModal({ content: <GenericError /> });
            } else if (invalidEmailError) {
                openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:invalid-email"
                        />
                    ),
                });
            } else if (maximumSizeReachedError) {
                openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:max-size-reached"
                        />
                    ),
                });
            } else if (duplicateEmailsError) {
                openModal({
                    content: (
                        <AddUserNotSuccessfulModalContent
                            title="users:users-table:modal-contents:add-user-not-successful:title"
                            description="users:users-table:modal-contents:add-user-not-successful:description:duplicate-emails"
                        />
                    ),
                });
            } else {
                openModal({ content: <GenericError /> });
            }
        },
    });

    //stores what user is currently writing
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setInputValue(event.target.value);
    };

    //stores submitted value
    const handleSendInviteButton = (
        event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>,
    ) => {
        event.preventDefault();
        setIsLoading(true);

        //CHECK IF IS SINGLE OR MULTIPLE USERS
        const arrayFromInputValue = inputValue?.split(",").map((email) => email.trim()) ?? [];
        const singleUser = arrayFromInputValue?.length === 1;
        const multipleUsers = arrayFromInputValue?.length > 1;

        if (singleUser) {
            mutationForSingleUser.mutate(inputValue, {
                onSettled: () => setIsLoading(false), //logic for the button only
            }); // POST request to addTrainee -> /trainee API { emailAddress: inputValue }
        } else if (multipleUsers) {
            mutationForMultipleUsers.mutate(arrayFromInputValue, {
                onSettled: () => setIsLoading(false), //logic for the button only
            }); // POST request to addTraineeBulk -> /trainee API { emailAddresses: addresses }
        }

        setInputValue("");
    };

    const handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.stopPropagation(); //stops button from making api request
            handleSendInviteButton(event);
        }
    };

    return (
        <ModalContentWrapperDiv>
            <Title>
                <FormattedMessage id={title} />
            </Title>
            <Description>
                <FormattedMessage id={description} />
            </Description>
            <ButtonsWrapper>
                <StyledInput
                    ref={inputRef}
                    placeholder="Email"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleEnterKey}
                />
                <StyledConfirmButton
                    onClick={handleSendInviteButton}
                    disabled={!inputValue || isLoading}
                    fullWidth
                >
                    {isLoading ? (
                        <Loader size="medium" color="#666666" fontSize="1.6875rem" />
                    ) : (
                        <FormattedMessage id="users:users-table:modal-contents:add-user:primary-button" />
                    )}
                </StyledConfirmButton>
            </ButtonsWrapper>
        </ModalContentWrapperDiv>
    );
};

export default AddUserModalContent;
